/*!
 * directed-widg-brew1.js
 *
 * Core + shell engine for "widg" widgets:
 *  - Core: DirectedWidgCore (version 1.0.0-brew1-widg)
 *      - mount(cfg)
 *      - mountFromConfig(cfg)  (alias)
 *      - bootstrapFromUrl(cfg)
 *  - Shell: directedWidgShellInit(options)
 *      - Theme + device toolbar
 *      - Collapsible logs panel
 *      - Uses DirectedWidgCore.mount(...) under the hood
 */

(function (global) {
    "use strict";

    const DOC = global.document;
    const VERSION = "1.0.0-brew1-widg";

    // ---------------------------------------------------------------------------
    // Helpers
    // ---------------------------------------------------------------------------

    function toList(raw) {
        try {
            if (!raw) return [];
            if (Array.isArray(raw)) return raw.map(String);
            const s = String(raw).trim();
            if (!s) return [];
            if (s[0] === "[") {
                const arr = JSON.parse(s);
                return Array.isArray(arr) ? arr.map(String) : [];
            }
            return s.split(",").map(v => v.trim()).filter(Boolean);
        } catch {
            return [];
        }
    }

    function listFromCfgOrUrl(cfg, qs, cfgKey, urlParam) {
        if (Array.isArray(cfg[cfgKey])) {
            return cfg[cfgKey].map(String);
        }
        const raw = (cfg[cfgKey] || qs.get(urlParam) || "").trim();
        return toList(raw);
    }

    function absURL(u) {
        return new URL(u, DOC.baseURI).href;
    }

    const isCSS = (u) => /\.css(?:$|[?#])/i.test(u);
    const isMJS = (u) => /\.mjs(?:$|[?#])/i.test(u);
    const isJS  = (u) => /\.(?:js|mjs)(?:$|[?#])/i.test(u);

    function makeLogger(cfg, qs) {
        const flagRaw = (cfg && cfg.debug != null)
            ? cfg.debug
            : (qs.get("data-debug") || "1");

        const debug = ["1","true","yes","on"].includes(
            String(flagRaw).toLowerCase()
        );

        return function log(tag, msg, extra) {
            if (!debug) return;
            if (cfg && typeof cfg.log === "function") {
                try {
                    cfg.log(tag, msg, extra);
                    return;
                } catch {
                    // fall back to console
                }
            }
            if (global.console && console.log) {
                console.log("[widg-core]", tag, msg, extra || "");
            }
        };
    }

    // ---------------------------------------------------------------------------
    // Core: mount()
    // ---------------------------------------------------------------------------

    function mount(rawCfg) {
        const cfg = rawCfg || {};
        const qs = new URLSearchParams(global.location.search || "");
        const log = makeLogger(cfg, qs);

        // ---------- normalize config --------------------------------------------
        let deedID = (
            cfg.deedID ||
            qs.get("deedID") ||
            qs.get("deedId") ||
            qs.get("deed") ||
            ""
        ).trim();
        if (!deedID) deedID = "directedWidg_" + Date.now();

        const shareList    = listFromCfgOrUrl(cfg, qs, "shareMaterials",    "directed-share-materials");
        const internalList = listFromCfgOrUrl(cfg, qs, "internalMaterials", "directed-internal-materials");
        const externalList = listFromCfgOrUrl(cfg, qs, "externalMaterials", "directed-external-materials");

        const props = { ...(cfg.props || {}) };

        // data-* from URL (?data-mintframe-src=/child.html ...)
        for (const [key, value] of qs.entries()) {
            if (key.startsWith("data-")) {
                props[key.slice(5)] = value;
            }
        }

        // JS config overrides URL
        if (cfg.mintframeSrc)          props["mintframe-src"]     = cfg.mintframeSrc;
        if (cfg.mintframeHeight)       props["mintframe-height"]  = cfg.mintframeHeight;
        if (cfg.loadingMessage)        props["loading-message"]   = cfg.loadingMessage;
        if (cfg.minHeight != null)     props["min-height"]        = String(cfg.minHeight);
        if (cfg.maxHeight != null)     props["max-height"]        = String(cfg.maxHeight);
        if (cfg.copyLinks != null)     props["copy-links"]        = cfg.copyLinks ? "1" : "0";
        if (cfg.seamlessPadding != null) props["seamless-padding"] = String(cfg.seamlessPadding);
        if (cfg.params)                props["params"]            = cfg.params;
        if (cfg.debug != null)         props["debug"]             = cfg.debug ? "1" : "0";

        const src     = props["mintframe-src"];
        const height  = props["mintframe-height"] || "600px";
        const loadMsg = props["loading-message"] || "";

        let pad = 8;
        if (props["seamless-padding"]) {
            const p = parseInt(props["seamless-padding"], 10);
            if (!Number.isNaN(p)) pad = p;
        }

        const minH      = props["min-height"] ? parseInt(props["min-height"], 10) : 120;
        const maxH      = props["max-height"] ? parseInt(props["max-height"], 10) : 3000;
        const copyLinks = (props["copy-links"] ?? "1") === "1";

        if (!src) {
            log("error", "missing mintframe src (mintframeSrc or ?data-mintframe-src=/child.html)");
            return;
        }

        if (cfg.expectedVersion && cfg.expectedVersion !== VERSION) {
            log("version", "mismatch expected=" + cfg.expectedVersion + " got=" + VERSION);
        }

        log("params", "deedID=" + deedID);
        log("params", "src=" + src);
        log("params", "shareList=" + JSON.stringify(shareList || []));

        // legacy mirror (Hugo v2/v3 compatibility)
        global.directed_widget_data_repo = global.directed_widget_data_repo || {};
        global.directed_widget_data_repo[deedID] = {
            props,
            shareList,
            internalList,
            externalList
        };

        // ---------- inject materials into parent --------------------------------
        function safeEscapeKey(key) {
            if (global.CSS && CSS.escape) return CSS.escape(String(key));
            return String(key).replace(/"/g, '\\"');
        }

        function ensureParentCSS(href, key) {
            if (!href) return;
            const abs = absURL(href);
            const doc = DOC;

            if (key && doc.querySelector(
                `link[data-seamless-key="${safeEscapeKey(key)}"]`
            )) return;

            const exists = Array.from(
                doc.querySelectorAll('link[rel="stylesheet"]')
            ).some(n => n.href === abs);
            if (exists) return;

            const ln = doc.createElement("link");
            ln.rel = "stylesheet";
            ln.href = href;
            if (key) ln.dataset.seamlessKey = key;
            doc.head.appendChild(ln);
            log("parent", "CSS " + href);
        }

        function ensureParentJS(srcUrl, key, isModule) {
            if (!srcUrl) return;
            const abs = absURL(srcUrl);
            const doc = DOC;

            if (key && doc.querySelector(
                `script[data-seamless-key="${safeEscapeKey(key)}"]`
            )) return;

            const exists = Array.from(
                doc.querySelectorAll("script[src]")
            ).some(n => n.src === abs);
            if (exists) return;

            const sc = doc.createElement("script");
            if (isModule) sc.type = "module";
            sc.src = srcUrl;
            if (key) sc.dataset.seamlessKey = key;
            doc.body.appendChild(sc);
            log("parent", "JS " + srcUrl);
        }

        function pushAsset(url, bucket) {
            const key = bucket + "::" + url;
            if (isCSS(url)) ensureParentCSS(url, key);
            else if (isJS(url)) ensureParentJS(url, key, isMJS(url));
        }

        (shareList    || []).forEach(u => pushAsset(u, "share"));
        (internalList || []).forEach(u => pushAsset(u, "internal"));
        (externalList || []).forEach(u => pushAsset(u, "external"));

        // ---------- build container + iframe ------------------------------------
        const container = DOC.createElement("div");
        container.id = "directed-mintframe-container-" + deedID;
        container.className = "directed-mintframe-container";
        container.style.position = "relative";
        container.style.width = "100%";
        container.style.minHeight = height;
        container.style.height = "auto";
        container.style.overflow = "visible";
        container.style.margin = "0";
        container.style.padding = "0";

        const loader = DOC.createElement("div");
        loader.id = "loader-" + deedID;
        loader.className = "directed-mintframe-loader";
        loader.innerHTML = `
<div class="directed-mintframe-loader-balls">
  <span style="opacity:.9;"></span>
  <span style="opacity:.7;"></span>
  <span style="opacity:.5;"></span>
</div>
${loadMsg ? `<div class="directed-mintframe-loader-text">${loadMsg}</div>` : ""}`;

        const iframe = DOC.createElement("iframe");
        iframe.id   = deedID;
        iframe.name = deedID;
        iframe.src  = src;
        iframe.className = "directed-mintframe-iframe";
        iframe.loading = "lazy";
        iframe.style.border = "none";
        iframe.style.width = "100%";
        iframe.style.height = "0";
        iframe.style.display = "block";

        iframe.dataset.min = String(minH);
        iframe.dataset.max = String(maxH);
        iframe.dataset.pad = String(pad);
        iframe.dataset.copyLinks = copyLinks ? "1" : "0";
        if (props.params) iframe.setAttribute("data-params", props.params);
        if (props.debug)  iframe.setAttribute("data-debug", "1");

        container.appendChild(loader);
        container.appendChild(iframe);

        const mountTargetId = cfg.mountTargetId || cfg.containerId || cfg.mountIntoId;
        let ph = DOC.getElementById(deedID);
        let mounted = false;

        if (mountTargetId) {
            const mt = DOC.getElementById(mountTargetId);
            if (mt) {
                mt.innerHTML = "";
                mt.appendChild(container);
                mounted = true;
                log("mount", "attached into #" + mountTargetId);
            }
        }

        if (!mounted && ph && ph.parentNode) {
            ph.parentNode.insertBefore(container, ph.nextSibling);
            mounted = true;
            log("mount", "inserted after #" + deedID);
        }

        if (!mounted) {
            DOC.body.appendChild(container);
            log("mount", "appended to <body>");
        }

        if (ph && cfg.hidePlaceholder !== false) {
            ph.style.display = "none";
        }

        // ---------- MERGE_STATE bus ---------------------------------------------
        function legacyComposePayload() {
            return {
                deedID,
                top_url: global.location.href,
                referrer: DOC.referrer || null,
                timestamp: Date.now(),
                props,
                shareList,
                internalList,
                externalList
            };
        }

        function legacySendPayload() {
            const msg = {
                sig: "directed_mintframe_v3",
                type: "MERGE_STATE",
                payload: legacyComposePayload()
            };
            try {
                if (iframe.contentWindow) {
                    iframe.contentWindow.postMessage(msg, "*");
                    log("pm", "MERGE_STATE posted");
                }
            } catch (err) {
                log("pm", "MERGE_STATE failed", err && err.message);
            }
        }

        let pmTimer = null;
        const alive = () =>
            !!(DOC.body && DOC.body.contains(iframe));

        function waitForSeamlessReady(maxMs) {
            const duration = maxMs || 2000;
            const start = (global.performance && performance.now)
                ? performance.now()
                : Date.now();

            return new Promise((resolve) => {
                const iv = setInterval(() => {
                    const now = (global.performance && performance.now)
                        ? performance.now()
                        : Date.now();
                    if (global.Seamless && typeof global.Seamless.attachToIframe === "function") {
                        clearInterval(iv);
                        resolve(true);
                    } else if (now - start > duration) {
                        clearInterval(iv);
                        resolve(false);
                    }
                }, 20);
            });
        }

        function manualInjectIntoChild() {
            let cd = null;
            try {
                cd = iframe.contentDocument ||
                    (iframe.contentWindow && iframe.contentWindow.document) ||
                    null;
            } catch {
                cd = null;
            }
            if (!cd) {
                log("fallback", "XO → skip manual inject");
                return;
            }
            log("fallback", "SO → inject CSS/JS into child");

            const seen = new Set();

            const addCSS = (href) => {
                const abs = new URL(href, DOC.baseURI).href;
                if (seen.has("css:" + abs)) return;
                seen.add("css:" + abs);
                if (cd.querySelector(`link[rel="stylesheet"][href="${abs}"]`)) return;
                const ln = cd.createElement("link");
                ln.rel = "stylesheet";
                ln.href = href;
                ln.dataset.seamlessKey = "fallback";
                cd.head.appendChild(ln);
            };

            const addJS = (srcUrl, isModule) => {
                const abs = new URL(srcUrl, DOC.baseURI).href;
                if (seen.has("js:" + abs)) return;
                seen.add("js:" + abs);
                if (cd.querySelector(`script[src="${abs}"]`)) return;
                const sc = cd.createElement("script");
                if (isModule) sc.type = "module";
                sc.src = srcUrl;
                sc.dataset.seamlessKey = "fallback";
                cd.body.appendChild(sc);
            };

            const push = (url) => {
                if (isCSS(url)) addCSS(url);
                else if (isJS(url)) addJS(url, isMJS(url));
            };

            (shareList    || []).forEach(push);
            (internalList || []).forEach(push);
            (externalList || []).forEach(push);
        }

        iframe.addEventListener("load", async () => {
            log("iframe", "load");

            legacySendPayload();
            if (pmTimer) clearInterval(pmTimer);
            pmTimer = global.setInterval(() => {
                if (alive()) legacySendPayload();
                else clearInterval(pmTimer);
            }, 5000);

            DOC.addEventListener("visibilitychange", () => {
                if (!DOC.hidden) legacySendPayload();
            });

            loader.style.opacity = "0";
            global.setTimeout(() => {
                loader.style.display = "none";
            }, 300);

            const hasSeamless = await waitForSeamlessReady(2000);
            if (hasSeamless) {
                log("Seamless", "attachToIframe");
                try {
                    const bridge = await global.Seamless.attachToIframe(iframe, {
                        copyHead: { styles: true, inline: false },
                        seamlessPadding: pad
                    });

                    const childStyles = [];
                    const childScripts = [];
                    const pushChildAsset = (url, bucket) => {
                        const key = bucket + "::" + url;
                        if (isCSS(url)) childStyles.push({ href: url, key });
                        else if (isJS(url)) childScripts.push({ src: url, key, module: isMJS(url) });
                    };

                    (shareList    || []).forEach(u => pushChildAsset(u, "share"));
                    (internalList || []).forEach(u => pushChildAsset(u, "internal"));
                    (externalList || []).forEach(u => pushChildAsset(u, "external"));

                    await bridge.apply({
                        styles:  childStyles,
                        scripts: childScripts,
                        calls:   props.purpose
                            ? [{ fn: "setTitle", args: [String(props.purpose)] }]
                            : []
                    });
                    log("Seamless", "apply OK");
                } catch (err) {
                    log("Seamless", "apply failed → manualInjectIntoChild", err && err.message);
                    manualInjectIntoChild();
                }
            } else {
                log("Seamless", "not detected → manualInjectIntoChild");
                manualInjectIntoChild();
            }
        });

        global.addEventListener("beforeunload", () => {
            if (pmTimer) clearInterval(pmTimer);
        });

        log("init", "core mount complete", "version=" + VERSION);
    }

    function bootstrapFromUrl(extraCfg) {
        const cfg = extraCfg || {};
        mount(cfg);
    }

    // Exposed core API
    global.DirectedWidgCore = {
        version: VERSION,
        mount,
        mountFromConfig: mount,
        bootstrapFromUrl
    };
})(window);

// ---------------------------------------------------------------------------
// Shell: directedWidgShellInit (demo / preview toolbar)
// ---------------------------------------------------------------------------

window.directedWidgShellInit = function directedWidgShellInit(options) {
    const cfg = options || {};
    const qs  = new URLSearchParams(window.location.search || "");

    // ---------- logging panel -------------------------------------------------
    const logsShell   = document.getElementById("logsShell");
    const logsBodyBox = logsShell
        ? logsShell.querySelector(".logs-body")
        : null;
    const logsBody    = document.getElementById("logsBody");
    const logsToggle  = document.getElementById("logsToggleBtn");
    const logsChevron = document.getElementById("logsChevron");

    function log(tag, msg, extra) {
        if (logsBody && logsBodyBox) {
            const line = document.createElement("div");
            line.className = "log-line";

            const tSpan = document.createElement("span");
            tSpan.className = "t";
            tSpan.textContent = new Date().toLocaleTimeString();

            const tagSpan = document.createElement("span");
            tagSpan.className = "tag";
            tagSpan.textContent = " [" + tag + "] ";

            const msgSpan = document.createElement("span");
            msgSpan.className = "msg";
            msgSpan.textContent = msg + (extra ? " " + extra : "");

            line.appendChild(tSpan);
            line.appendChild(tagSpan);
            line.appendChild(msgSpan);

            logsBody.appendChild(line);
            logsBodyBox.scrollTop = logsBodyBox.scrollHeight;
        }
        console.log("[widg-shell]", tag, msg, extra || "");
    }

    if (logsToggle && logsShell && logsChevron) {
        logsToggle.addEventListener("click", () => {
            const open = logsShell.getAttribute("data-open") !== "0";
            logsShell.setAttribute("data-open", open ? "0" : "1");
            logsChevron.textContent = open ? "▸" : "▾";
            log("logs", open ? "collapsed" : "expanded");
        });
    }

    // ---------- toolbar controls ----------------------------------------------
    const themePresetSel = document.getElementById("themePreset");
    const themeBgInput   = document.getElementById("themeBg");
    const themeFgInput   = document.getElementById("themeFg");
    const deviceGroup    = document.getElementById("deviceGroup");
    const deviceShell    = document.getElementById("deviceShell");

    const badgeDeed   = document.getElementById("badgeDeed");
    const badgeSrc    = document.getElementById("badgeSrc");
    const previewMeta = document.getElementById("previewMeta");

    function applyThemeColors(bg, fg, preset) {
        const root = document.documentElement;
        root.style.setProperty("--shell-bg", bg);
        root.style.setProperty("--shell-fg", fg);

        let mode;
        if (preset === "dark")      mode = "dark";
        else if (preset === "brand") mode = "brand";
        else                         mode = "light";

        root.dataset.shellMode = mode;

        const detail = { preset, mode, bg, fg, ts: Date.now() };
        window.dispatchEvent(
            new CustomEvent("ifc:pure:v1:theme:parent-hint", { detail })
        );
        log("theme", `preset=${preset} mode=${mode}`, `bg=${bg} fg=${fg}`);
    }

    function applyThemePreset(preset) {
        let bg, fg;
        if (preset === "dark") {
            bg = "#020617"; fg = "#e5e7eb";
        } else if (preset === "brand") {
            bg = "#030712"; fg = "#f97316";
        } else {
            bg = "#f9fafb"; fg = "#020617";
        }
        if (themeBgInput) themeBgInput.value = bg;
        if (themeFgInput) themeFgInput.value = fg;
        applyThemeColors(bg, fg, preset);
    }

    if (themePresetSel) {
        themePresetSel.addEventListener("change", () => {
            applyThemePreset(themePresetSel.value);
        });
    }
    if (themeBgInput && themeFgInput && themePresetSel) {
        themeBgInput.addEventListener("input", () => {
            applyThemeColors(themeBgInput.value, themeFgInput.value, themePresetSel.value);
        });
        themeFgInput.addEventListener("input", () => {
            applyThemeColors(themeBgInput.value, themeFgInput.value, themePresetSel.value);
        });
    }

    const prefersDark = window.matchMedia &&
        window.matchMedia("(prefers-color-scheme: dark)").matches;
    if (prefersDark && themePresetSel) {
        themePresetSel.value = "dark";
    }
    applyThemePreset(themePresetSel ? themePresetSel.value : "light");

    // Device width
    function setDevice(device) {
        let width;
        if (device === "mobile")      width = "420px";
        else if (device === "tablet") width = "900px";
        else                          width = "100%";

        document.documentElement.style.setProperty("--device-width", width);

        if (deviceGroup) {
            Array.from(deviceGroup.querySelectorAll(".device-btn"))
                .forEach(btn => {
                    btn.dataset.active = (btn.dataset.device === device) ? "1" : "0";
                });
        }
        log("device", "set " + device, width);
    }

    if (deviceGroup) {
        deviceGroup.addEventListener("click", (e) => {
            const btn = e.target.closest(".device-btn");
            if (!btn) return;
            const dev = btn.dataset.device;
            if (!dev) return;
            setDevice(dev);
        });
    }
    setDevice("desktop");

    // ---------- summarize current config in badges ----------------------------
    let deedID = (
        cfg.deedID ||
        qs.get("deedID") ||
        qs.get("deedId") ||
        qs.get("deed") ||
        ""
    ).trim();
    if (!deedID) deedID = "directedWidgShell_" + Date.now();
    cfg.deedID = cfg.deedID || deedID;

    const src = cfg.mintframeSrc ||
        qs.get("data-mintframe-src") ||
        "/app.html";

    const pad = (cfg.seamlessPadding != null)
        ? cfg.seamlessPadding
        : 8;
    const minH = (cfg.minHeight != null)
        ? cfg.minHeight
        : 120;
    const maxH = (cfg.maxHeight != null)
        ? cfg.maxHeight
        : 3000;
    const height = cfg.mintframeHeight || "600px";

    if (badgeDeed) badgeDeed.textContent = "deed=" + deedID;
    if (badgeSrc)  badgeSrc.textContent  = "src=" + src;
    if (previewMeta) {
        previewMeta.textContent = `height=${height}, pad=${pad}, clamp=[${minH}, ${maxH}]`;
    }

    log("init", "shell ready", window.location.search || "?");

    // ---------- log interesting postMessages from child libs ------------------
    window.addEventListener("message", (e) => {
        const d = e.data || {};
        try {
            if (d && typeof d === "object") {
                if (d.sig === "IFC-THEME-PURE1") {
                    log("pm/theme", d.type || "event",
                        JSON.stringify(d).slice(0, 140));
                } else if (d.sig === "IFC-AUTO3") {
                    log("pm/auto", d.type || "event",
                        JSON.stringify(d).slice(0, 140));
                } else if (d.sig === "directed_mintframe_v3") {
                    log("pm/widg", d.type || "event",
                        JSON.stringify(d.payload || {}).slice(0, 140));
                }
            }
        } catch (err) {
            log("pm", "parse-error", err.message);
        }
    });

    // ---------- hand off to core (DirectedWidgCore.mount) ---------------------
    const coreCfg = Object.assign({}, cfg, {
        deedID,
        mintframeSrc: src,
        mintframeHeight: height,
        seamlessPadding: pad,
        minHeight: minH,
        maxHeight: maxH,
        mountTargetId: cfg.mountTargetId || (deviceShell ? "deviceShell" : undefined),
        log: (tag, msg, extra) => log(tag, msg, extra),
        expectedVersion: cfg.expectedVersion || "4.0.0-brew1-widg"
    });

    function tryMount(attempt) {
        attempt = attempt || 0;
        const core = window.DirectedWidgCore;
        if (core && typeof core.mount === "function") {
            core.mount(coreCfg);
            log("core", "mount", "ok");
            return;
        }
        if (attempt < 50) {
            setTimeout(() => tryMount(attempt + 1), 40);
        } else {
            log("error", "DirectedWidgCore not available; check script src");
        }
    }

    tryMount();
};

// Optional backwards-compat alias for older demos
window.directedWidgetDemoInit = window.directedWidgShellInit;
